import { atom, RecoilState, selector, selectorFamily } from 'recoil';
import {
  FormTemplateType,
  FormTemplate,
  FieldTemplate,
  LayoutItem,
} from 'api/models';

interface Layout {
  children: LayoutItem[];
}

export const formTemplateTypeListState: RecoilState<
  FormTemplateType[] | undefined
> = atom<FormTemplateType[] | undefined>({
  key: 'formTemplateTypeList',
  default: [],
});

export const sampleTemplateTypeListState: RecoilState<
  FormTemplateType[] | undefined
> = atom<FormTemplateType[] | undefined>({
  key: 'sampleTemplateTypeList',
  default: [],
});

export const selectedFormTemplateTypeState: RecoilState<FormTemplateType> =
  atom<FormTemplateType>({
    key: 'formTemplateType',
    default: undefined,
  });

export const formTemplateState = atom<FormTemplate | undefined>({
  key: 'formTemplate',
  default: undefined,
});

export const layoutAtom: RecoilState<Layout> = atom<Layout>({
  key: 'items',
  default: {
    children: [],
  },
});

export const layoutSelector = selector<Layout>({
  key: 'layoutItems',
  get: ({ get }) => {
    const items = get(layoutAtom);
    return items;
  },
  set: ({ set }, newValue) => {
    set(layoutAtom, newValue);
  },
});

export const fieldsAtom: RecoilState<FieldTemplate[]> = atom<FieldTemplate[]>({
  key: 'fields',
  default: [],
});

export const fieldTemplatesState = selector<FieldTemplate[]>({
  key: 'fieldTemplates',
  get: ({ get }) => {
    const fields = get(fieldsAtom);
    return fields;
  },
  set: ({ set }, newValue) => {
    set(fieldsAtom, newValue);
  },
});

export const fieldTemplateAliases = selector({
  key: 'fieldTemplateAliases',
  get: ({ get }) => {
    const fieldTemplates = get(fieldTemplatesState);
    return fieldTemplates.map(fieldTemplate => fieldTemplate.alias ?? '');
  },
});

export const formTemplateVersionsState = atom<FormTemplate[]>({
  key: 'formTemplateVersions',
  default: [],
});

export const fieldByIdSelector = selectorFamily<FieldTemplate, string>({
  key: 'fieldByIdSelector',
  get:
    id =>
    ({ get }) =>
      get(fieldsAtom).find(field => field.id === id)!,
});

export const isFormSavingAtom = atom<boolean>({
  key: 'isSaving',
  default: false,
});

// this atom is used to store the last added field template, so that we can scroll to it after adding it
export const lastAddedLayoutItem = atom<LayoutItem | undefined>({
  key: 'lastAddedFieldTemplate',
  default: undefined,
});

export const fieldTemplateByIdAndSearch = selectorFamily<
  FieldTemplate[] | undefined,
  { alias: string; search: string }
>({
  key: 'fieldTemplateByIdAndSearch',
  get:
    ({ alias, search }) =>
    ({ get }) => {
      const fields = get(fieldsAtom).filter(f => f.alias !== alias);

      if (!search) {
        return fields;
      }

      return fields.filter(f =>
        f.fieldAttributes.label
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase()),
      );
    },
});
